// =============================================================================
// BP-08-05: Vendor Portal Frontend - ProductCard Component
// =============================================================================

import React from 'react';
import { Link } from 'react-router-dom';
import { MoreVertical, Edit, Trash2, Eye, EyeOff, Star, TrendingUp } from 'lucide-react';
import type { VendorProduct, ProductStatus } from '@/features/vendor/types/vendorTypes';

interface ProductCardProps {
  product: VendorProduct;
  onStatusChange?: (productId: string, status: ProductStatus) => void;
  onDelete?: (productId: string) => void;
}

const statusConfig: Record<ProductStatus, { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'bg-gray-100 text-gray-700' },
  active: { label: 'Active', className: 'bg-green-100 text-green-700' },
  out_of_stock: { label: 'Out of Stock', className: 'bg-amber-100 text-amber-700' },
  discontinued: { label: 'Discontinued', className: 'bg-red-100 text-red-700' },
};

export function ProductCard({ product, onStatusChange, onDelete }: ProductCardProps) {
  const [showMenu, setShowMenu] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Close menu on click outside
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatPrice = () => {
    if (product.priceType === 'quote') return 'Request Quote';
    if (product.priceType === 'call') return 'Call for Price';
    if (product.priceMin && product.priceMax) {
      return `$${product.priceMin.toLocaleString()} - $${product.priceMax.toLocaleString()}`;
    }
    if (product.priceMin) {
      return `$${product.priceMin.toLocaleString()}`;
    }
    return 'Price not set';
  };

  const status = statusConfig[product.status];

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      {/* Image */}
      <div className="relative aspect-[4/3] bg-cream-dark">
        {product.imageUrls[0] ? (
          <img
            src={product.imageUrls[0]}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-4xl text-gray-300">📦</span>
          </div>
        )}

        {/* Status Badge */}
        <span
          className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-semibold ${status.className}`}
        >
          {status.label}
        </span>

        {/* Featured Badge */}
        {product.isFeatured && (
          <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-semibold bg-terracotta text-white">
            Featured
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Category */}
        <p className="text-xs font-medium text-terracotta uppercase tracking-wide mb-1">
          {product.categoryName}
        </p>

        {/* Name */}
        <h3 className="font-display font-semibold text-navy mb-2 line-clamp-2">
          {product.name}
        </h3>

        {/* Price */}
        <p className="text-lg font-semibold text-navy mb-3">{formatPrice()}</p>

        {/* Stats */}
        <div className="flex items-center gap-4 text-sm text-warm-gray mb-4">
          <div className="flex items-center gap-1">
            <Eye className="w-4 h-4" />
            <span>{product.viewCount}</span>
          </div>
          <div className="flex items-center gap-1">
            <Star className="w-4 h-4 text-amber-400" />
            <span>{product.avgRating.toFixed(1)}</span>
            <span className="text-gray-400">({product.reviewCount})</span>
          </div>
          <div className="flex items-center gap-1">
            <TrendingUp className="w-4 h-4" />
            <span>{product.inquiryCount}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Link
            to={`/vendor/products/${product.id}/edit`}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-cream-dark text-navy font-medium rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Edit className="w-4 h-4" />
            Edit
          </Link>

          {/* More Menu */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="More options"
            >
              <MoreVertical className="w-5 h-5 text-gray-500" />
            </button>

            {showMenu && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-10">
                <Link
                  to={`/marketplace/products/${product.slug}`}
                  target="_blank"
                  className="flex items-center gap-2 px-4 py-2 text-sm text-navy hover:bg-gray-50"
                >
                  <Eye className="w-4 h-4" />
                  View in Marketplace
                </Link>

                {product.status === 'active' ? (
                  <button
                    onClick={() => {
                      onStatusChange?.(product.id, 'draft');
                      setShowMenu(false);
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-navy hover:bg-gray-50"
                  >
                    <EyeOff className="w-4 h-4" />
                    Set as Draft
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      onStatusChange?.(product.id, 'active');
                      setShowMenu(false);
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-navy hover:bg-gray-50"
                  >
                    <Eye className="w-4 h-4" />
                    Publish
                  </button>
                )}

                <hr className="my-1 border-gray-100" />

                <button
                  onClick={() => {
                    onDelete?.(product.id);
                    setShowMenu(false);
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Product
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductCard;

